/**
 * How much a calendar block may say at the height it happens to be.
 *
 * The vertical counterpart of the board's `SegmentCell`, which runs the same ladder on width. The
 * rule both share: a block never renders a row it cannot finish. The old worker blocks rendered a
 * fixed layout at every duration and let `overflow-hidden` decide what survived, which is how a
 * 30-minute job came to show its address sliced horizontally through the middle of the glyphs.
 *
 * So the ladder counts whole line boxes rather than guessing at tiers, and the tiers are named
 * afterwards. Anything this returns, the block can draw in full.
 *
 * Pure — no React, no DOM measurement. The heights come from the grid geometry, which is known
 * before render.
 */

/** One text row: a 12–13px glyph sitting on a 13px line box. Every row a block draws is this tall. */
export const LINE_PX = 13

/**
 * Vertical padding a text-bearing segment reserves, top and bottom together.
 *
 * 8 against 3px + 3px of actual padding, so the 1px rule between two strips is already paid for and
 * the ladder still has a pixel of slack. The slack is the point: it is what keeps the last row a
 * whole row.
 */
export const SEGMENT_PAD_Y = 8

export type WorkDensity = 'bare' | 'single' | 'compact' | 'full'

export interface WorkFit {
  tier: WorkDensity
  /** Whole rows that fit. A count, never a clamp — there is no partial row. */
  lines: number
}

/**
 * One text row in the one-day view. That view has the width to set its type larger — 16/15/14px
 * against 13/12/11 — and a larger glyph needs a taller line box, so the ladder counts with this
 * instead of `LINE_PX` there. The price is rows: a 30-minute job holds two of these where it held
 * three of the small ones, which is the right trade for a phone held at arm's length.
 */
export const WIDE_LINE_PX = 17

/** Whole rows of `linePx` that fit in `heightPx` once padding is taken out. Never negative, never partial. */
export function fitLines(heightPx: number, linePx: number = LINE_PX): number {
  return Math.max(0, Math.floor((heightPx - SEGMENT_PAD_Y) / linePx))
}

/**
 * The work segment's budget.
 *
 * At 96px per hour the thresholds land at: under 14 minutes `bare`, 14–21 `single`, 22–29 `compact`,
 * 30 minutes and up `full`. A half-hour job — the commonest length on this calendar — is exactly the
 * point where the tenant's name earns its row.
 */
export function resolveWorkDensity(heightPx: number, linePx: number = LINE_PX): WorkFit {
  const lines = fitLines(heightPx, linePx)
  if (lines <= 0) return { tier: 'bare', lines: 0 }
  if (lines === 1) return { tier: 'single', lines: 1 }
  if (lines === 2) return { tier: 'compact', lines: 2 }
  return { tier: 'full', lines }
}

/**
 * Below this a travel strip carries no mark at all — a bare sliver of colour, which is still the
 * honest thing to draw: it says "there is a drive here" at any height down to a pixel.
 */
export const STRIP_ICON_MIN = 14

/** Below this the strip keeps its icon but drops the word. */
export const STRIP_LABEL_MIN = 24

/**
 * The same two steps for the one-day view's larger icon and label. The label step stays at 24: a
 * 17px line box plus 3px of padding above and below is 23, so the commonest drive — 15 minutes,
 * 24px of grid — keeps its word and its length rather than dropping to an icon.
 */
export const WIDE_STRIP_ICON_MIN = 16
export const WIDE_STRIP_LABEL_MIN = 24

export type StripDensity = 'bare' | 'icon' | 'label'

/**
 * The Anfahrt / Abfahrt strip's budget.
 *
 * The icon tier is the one that earns its keep on a phone: a 10-minute drive is 16px tall and cannot
 * hold the word `Anfahrt`, but it holds a car at 12px — so driving stays distinguishable from
 * working without relying on hue alone.
 */
export function resolveStripDensity(heightPx: number, wide = false): StripDensity {
  // The one-day view draws a 14px icon and a 13px label, so the icon step is two pixels later.
  if (heightPx < (wide ? WIDE_STRIP_ICON_MIN : STRIP_ICON_MIN)) return 'bare'
  if (heightPx < (wide ? WIDE_STRIP_LABEL_MIN : STRIP_LABEL_MIN)) return 'icon'
  return 'label'
}

/** Thinnest a present segment may be drawn. A 2px sliver of blue still says "there is a drive here". */
export const SEGMENT_MIN_PX = 2

/**
 * Split a block's painted height between its segments so the parts sum to **exactly** the whole.
 *
 * This is the other half of the clipping fix. The old blocks sized each strip independently from its
 * own duration and gave each a `minHeight`, so on a short block the three strips plus their gaps
 * could add up to more than the box that contained them — and `overflow-hidden` ate the difference,
 * usually the Abfahrt. Sizing them against the box instead makes that arithmetically impossible.
 *
 * Every present segment keeps at least `SEGMENT_MIN_PX`; the remainder is shared by duration and the
 * rounding leftover goes to the longest segment, which is the one that can least notice it.
 */
export function distributeSegmentHeights(totalPx: number, minutes: number[]): number[] {
  const active = minutes.map((m) => (m > 0 ? m : 0))
  const totalMinutes = active.reduce((sum, m) => sum + m, 0)
  const count = active.filter((m) => m > 0).length
  const box = Math.max(0, Math.round(totalPx))

  if (count === 0 || totalMinutes <= 0 || box <= 0) return active.map(() => 0)

  // A block too short to give everyone the floor shrinks the floor rather than overflowing.
  const floor = Math.min(SEGMENT_MIN_PX, Math.floor(box / count))
  const spare = box - floor * count

  const out = active.map((m) => (m > 0 ? floor + Math.floor((m / totalMinutes) * spare) : 0))
  const longest = active.indexOf(Math.max(...active))
  out[longest] += box - out.reduce((sum, px) => sum + px, 0)
  return out
}

/**
 * How the running-job marker shows itself at a given work density.
 *
 * `pill` is the badge with its word, `dot` is the pulsing dot alone, `ring` is the white inset ring
 * that the block wears in every tier — the only marker left once there is no room for anything
 * inside. Mirrors what `SegmentCell` does at narrow widths on the board.
 */
export function resolveRunningMarker(tier: WorkDensity): 'pill' | 'dot' | 'ring' {
  if (tier === 'full') return 'pill'
  if (tier === 'bare') return 'ring'
  return 'dot'
}
